import { Breadcrumb, HeaderToolbar } from "@xola/ui-kit";
import React, { useMemo } from "react";
import { ExperienceStatus, Merchandise } from "types";
import { Page } from "../Page";
import { ListFilter } from "@/components/Dashboard/Filters/ListFilter";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";
import { InventoryStatusFilter } from "@/components/Product/MerchandiseInventory/InventoryStatusFilter";
import { InventoryTable } from "@/components/Product/MerchandiseInventory/InventoryTable";
import { ProductSearch } from "@/components/Product/ProductSearch";
import { useInventoryLots } from "@/hooks/resources/useInventoryLots";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useSearch } from "@/hooks/useSearch";
import { isInventoryTracked, MERCHANDISE_API_PATH } from "@/models/Merchandise";
import { useMerchandiseInventoryFilterStore } from "@/stores/merchandiseInventoryFilterStore";

export const MerchandiseInventoryPage = () => {
    const { search, setSearch, merchandiseSelection, setMerchandiseSelection, statusFilter, setStatusFilter } =
        useMerchandiseInventoryFilterStore();

    const selectedIds = merchandiseSelection.all ? [] : merchandiseSelection.selected;
    const { data: merchandises = [] } = useAutoFetchCollection<Merchandise>(MERCHANDISE_API_PATH);
    const trackedMerchandises = useMemo(() => merchandises.filter((m) => isInventoryTracked(m)), [merchandises]);
    const selectedProducts = selectedIds
        .map((id) => merchandises.find((m) => m.id === id))
        .filter((m): m is Merchandise => m !== undefined)
        .map((m) => ({ id: m.id, name: m.name, photoId: m.photo?.id }));

    const trackedMerchandiseEntries = useMemo(
        () => trackedMerchandises.map((m) => ({ id: m.id, name: m.name, photoId: m.photo?.id })),
        [trackedMerchandises],
    );

    const {
        data: lots,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInventoryLots(selectedProducts, trackedMerchandiseEntries);
    const filtered = useSearch(lots, (lot) => [lot.productName, lot.sku, lot.variantLabel], search);

    const statusFiltered = useMemo(
        () =>
            statusFilter === "all"
                ? filtered
                : filtered.filter((lot) =>
                      statusFilter === "draft" ? lot.isDraft && !lot.isDeleted : !lot.isDeleted && !lot.isDraft,
                  ),
        [filtered, statusFilter],
    );

    return (
        <Page title="Inventory">
            <ColumnLayout>
                <HeaderToolbar className="justify-between">
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Inventory</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>

                    <ProductSearch onSearch={setSearch} />
                </HeaderToolbar>

                <div className="w-full divide-y divide-gray-lighter">
                    <div className="flex w-full items-center space-x-2 py-2 px-6 xl:px-10">
                        <InventoryStatusFilter value={statusFilter} onChange={setStatusFilter} />
                        <ListFilter
                            label="Merchandise"
                            title="Filter Merchandise"
                            localize={false}
                            collection={[MERCHANDISE_API_PATH]}
                            value={merchandiseSelection}
                            show={{ hideDeletedToggle: true }}
                            hideDeletedToggleLabel="Hide draft items"
                            hiddenItemBadgeLabel="Draft"
                            filterItem={(item) => isInventoryTracked(item as Merchandise)}
                            isItemDeleted={(item) => (item as Merchandise).status === ExperienceStatus.DRAFT}
                            onChange={(selection) => setMerchandiseSelection(selection)}
                        />
                    </div>
                </div>

                <ColumnLayout.Fill>
                    <InventoryTable
                        showLedgerAction
                        lots={statusFiltered}
                        isLoading={isLoading}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        onLoadMore={fetchNextPage}
                    />
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
